import { useEffect, useState } from 'react';
import { Zap, Sparkles, Shield } from 'lucide-react';
import axios from 'axios';
import PageHeader from '../components/PageHeader';
import { shouldUseAppleIAP, getCreditPackages, getAppleProductIdForCredits } from '../services/platform.service';
import { purchaseCredits } from '../services/apple-iap.service';

const API_URL = import.meta.env.VITE_API_URL || '';

function PurchaseForm() {
  const [selectedCredits, setSelectedCredits] = useState(50);
  const [processing, setProcessing] = useState(false);
  const [balance, setBalance] = useState<any>(null);

  const isAppleIAP = shouldUseAppleIAP();
  const creditPackages = getCreditPackages();

  useEffect(() => {
    fetchBalance();
  }, []);

  const fetchBalance = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_URL}/api/subscriptions/credits/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBalance(response.data);
    } catch (error) {
      console.error('Error fetching balance:', error);
    }
  };

  const selectedPackage = creditPackages.find(p => p.credits === selectedCredits);

  const handlePurchase = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPackage) return;

    setProcessing(true);

    try {
      // ===== iOS: Apple In-App Purchase =====
      if (isAppleIAP) {
        const appleProductId = getAppleProductIdForCredits(selectedCredits);
        if (!appleProductId) {
          alert('Pachetul selectat nu este disponibil.');
          setProcessing(false);
          return;
        }

        const result = await purchaseCredits(appleProductId);
        if (result.success) {
          alert(`${result.credits || selectedCredits} credite adăugate cu succes!`);
          await fetchBalance();
        } else {
          alert(result.error || 'Eroare la achiziția prin Apple.');
        }
        setProcessing(false);
        return;
      }

      // ===== Android/Web: Stripe =====
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(
        `${API_URL}/api/subscriptions/credits/checkout`,
        { credits: selectedCredits },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      window.location.href = response.data.url;
    } catch (error: any) {
      console.error('Error processing payment:', error);
      alert(error.response?.data?.error || 'Eroare la procesarea plății');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Current Balance Card */}
      {balance && (
        <div className="mb-8 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl shadow-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-5 h-5" />
                <span className="text-sm font-medium opacity-90">Soldul Tău Actual</span>
              </div>
              <div className="text-5xl font-bold mb-1">{Number.isInteger(balance.creditsRemaining) ? balance.creditsRemaining : parseFloat(balance.creditsRemaining.toFixed(2))}</div>
              <div className="text-blue-100 text-sm">
                din {balance.monthlyCredits} credite incluse în planul {balance.tier}
              </div>
            </div>
            <div className="text-right">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-lg backdrop-blur">
                <Sparkles className="w-5 h-5" />
                <span className="font-semibold">Nivel {balance.tier}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Credit Packages */}
      <h3 className="text-xl font-bold mb-6 text-gray-900 flex items-center gap-2">
        <Zap className="w-6 h-6 text-blue-600" />
        Alege Pachetul de Credite
      </h3>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {creditPackages.map((pkg) => {
          const isSelected = selectedCredits === pkg.credits;
          const isPopular = pkg.credits === 50;
          const isBestValue = pkg.credits === 200;

          return (
            <button
              key={pkg.credits}
              onClick={() => setSelectedCredits(pkg.credits)}
              className={`relative p-6 rounded-xl border-2 transition-all hover:scale-105 ${
                isSelected
                  ? 'border-blue-500 bg-blue-50 shadow-lg ring-2 ring-blue-200'
                  : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-md'
              }`}
            >
              {isPopular && (
                <div className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                  POPULAR
                </div>
              )}
              {isBestValue && (
                <div className="absolute -top-2 -right-2 bg-purple-500 text-white text-xs px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  BEST
                </div>
              )}
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900 mb-1">
                  {pkg.credits}
                </div>
                <div className="text-sm text-gray-600 font-medium mb-2">
                  credite
                </div>
                <div className="text-lg font-bold text-blue-600">
                  {pkg.priceLabel}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  €{(pkg.price / pkg.credits).toFixed(2)}/credit
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Purchase Button */}
      <form onSubmit={handlePurchase} className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-lg font-bold text-gray-900">
              {selectedPackage?.credits || 0} Credite Extra
            </div>
            <div className="text-sm text-gray-600">
              {isAppleIAP
                ? 'Plata se procesează securizat prin Apple.'
                : 'Vei fi redirecționat către Stripe pentru o plată securizată.'}
            </div>
          </div>
          <div className="text-3xl font-bold text-blue-600">
            {selectedPackage?.priceLabel || '€0.00'}
          </div>
        </div>

        <button
          type="submit"
          disabled={processing || !selectedPackage}
          className="w-full py-4 bg-blue-600 text-white rounded-lg font-bold text-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all hover:shadow-xl flex items-center justify-center gap-2"
        >
          {processing ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Se procesează...
            </>
          ) : (
            <>
              <Zap className="w-5 h-5" />
              Cumpără {selectedPackage?.credits || 0} Credite — {selectedPackage?.priceLabel || '€0.00'}
            </>
          )}
        </button>

        <div className="mt-4 flex items-center justify-center gap-4">
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <Shield className="w-4 h-4 text-green-600" />
            <span>Plată 100% securizată prin {isAppleIAP ? 'Apple' : 'Stripe'}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <Zap className="w-4 h-4 text-blue-600" />
            <span>Creditele sunt adăugate instant</span>
          </div>
        </div>
      </form>

      {/* Info */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-5">
        <p className="text-xs text-blue-800 leading-relaxed">
          <strong>ℹ️ Informații:</strong> Creditele extra nu expiră și se pot folosi oricând. 
          Se adaugă peste creditele incluse în abonament. 
          {isAppleIAP
            ? ' Plata se procesează prin Apple. Pentru asistență, contactați office@fiifit.ro.'
            : ' Prețurile afișate includ TVA. Pentru rambursări, contactați office@fiifit.ro în termen de 14 zile.'}
        </p>
      </div>
    </div>
  );
}

export default function ExtraCreditsPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Cumpără Credite Extra"
        subtitle="Adaugă credite suplimentare când ai nevoie de mai mult AI"
        backTo="/dashboard"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PurchaseForm />
      </div>
    </div>
  );
}
